import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ConfirmDialog,
  FormField,
  Modal,
  SecondaryButton,
} from "../../../components/UI/DesignStandards";
import { InlineLookup } from "../../../components/createTeam/components/InlineLookup";
import { notify } from "../../../components/UI/DesignStandards/Notifications/notify";
import useBowler from "../../../hooks/useBowler";
import { useOrganization } from "../../../context/OrganizationContext";
import { supabase } from "../../../supabaseClient";
import { searchOrgBowlersForTeamCreate } from "../../../utility/bowlerMatch";

const mergeLookupBowlers = (existingBowlers, tournamentBowlers) => {
  const bowlerMap = new Map();
  (existingBowlers || []).forEach((bowler) => {
    bowlerMap.set(bowler.id, bowler);
  });
  (tournamentBowlers || []).forEach((bowler) => {
    bowlerMap.set(bowler.id, bowler);
  });
  return Array.from(bowlerMap.values());
};

const ChangeBowlerModal = ({
  isOpen,
  onClose,
  onConfirm,
  currentBowler,
  takenBowlerIds = [],
  tournamentId,
}) => {
  const { existingBowlers } = useBowler();
  const { currentOrganization } = useOrganization();
  const [tournamentBowlers, setTournamentBowlers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [lookupResults, setLookupResults] = useState([]);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [pendingBowler, setPendingBowler] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setLookupResults([]);
      setLookupOpen(false);
      setPendingBowler(null);
      return;
    }

    let cancelled = false;
    const loadTournamentBowlers = async () => {
      if (!tournamentId || !currentOrganization?.id) {
        setTournamentBowlers([]);
        return;
      }
      try {
        const { data, error } = await supabase
          .from("tournamentAverages")
          .select(
            `
            average,
            "hasAverage",
            bowlers (
              id,
              name,
              "usbcID",
              bowlersContact (
                type,
                value
              )
            )
          `
          )
          .eq("tournamentID", tournamentId)
          .eq("bowlers.organizationID", currentOrganization.id);

        if (error) throw error;
        if (cancelled) return;

        const processed =
          data
            ?.map((item) => {
              const bowler = item.bowlers;
              if (!bowler) return null;
              const contacts = bowler.bowlersContact || [];
              return {
                id: bowler.id,
                name: bowler.name,
                usbcID: bowler.usbcID || "",
                average: item.average,
                hasAverage: item.hasAverage === false ? false : true,
                email: contacts.find((c) => c.type === "email")?.value || "",
                phone: contacts.find((c) => c.type === "phone")?.value || "",
                isNew: false,
              };
            })
            .filter(Boolean) || [];
        setTournamentBowlers(processed);
      } catch (error) {
        console.error("Could not load tournament bowlers for lookup:", error);
        if (!cancelled) setTournamentBowlers([]);
      }
    };

    loadTournamentBowlers();
    return () => {
      cancelled = true;
    };
  }, [isOpen, tournamentId, currentOrganization?.id]);

  const runSearch = useCallback(
    (term) => {
      const trimmed = term.trim();
      if (!trimmed) {
        setLookupResults([]);
        return;
      }
      const taken = takenBowlerIds.filter(Boolean);
      const filtered = searchOrgBowlersForTeamCreate({
        query: trimmed,
        usbcID: trimmed,
        orgBowlers: mergeLookupBowlers(existingBowlers, tournamentBowlers),
        excludeIds: taken,
        limit: 10,
      });
      setLookupResults(filtered);
    },
    [existingBowlers, tournamentBowlers, takenBowlerIds]
  );

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setLookupOpen(Boolean(value.trim()));
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      runSearch(value);
    }, 300);
  };

  useEffect(() => {
    if (!isOpen || !searchTerm.trim()) return;
    runSearch(searchTerm);
    // Refresh results after org/tournament bowlers finish loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingBowlers, tournamentBowlers, isOpen]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSelect = (bowler) => {
    if (bowler.id && bowler.id === currentBowler?.id) {
      notify.error("That person is already in this place.");
      return;
    }
    setPendingBowler(bowler);
  };

  const handleConfirm = async () => {
    if (!pendingBowler || confirming) return;
    setConfirming(true);
    try {
      await onConfirm(pendingBowler);
      setPendingBowler(null);
      onClose();
    } catch (error) {
      // Parent already notified.
    } finally {
      setConfirming(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal
        isOpen={isOpen && !pendingBowler}
        onClose={onClose}
        title="Change bowler"
        subtitle="Search for who should be in this place."
        maxWidth="max-w-lg"
        footer={
          <SecondaryButton type="button" onClick={onClose} className="w-full">
            Cancel
          </SecondaryButton>
        }
      >
        <FormField
          label="Name"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Enter bowler name"
          autoComplete="off"
        />
        <InlineLookup
          isOpen={lookupOpen && Boolean(searchTerm.trim())}
          searchTerm={searchTerm}
          results={lookupResults}
          onSelect={handleSelect}
          onClose={() => {
            setLookupOpen(false);
            setLookupResults([]);
          }}
        />
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(pendingBowler)}
        onClose={() => setPendingBowler(null)}
        onConfirm={handleConfirm}
        title="Change bowler"
        message="Scores and side pots will be reassigned to this bowler. Average will be updated to the new bowler if one exists. Please confirm all information is still accurate."
        confirmText="Change bowler"
        cancelText="Cancel"
        variant="warning"
        loading={confirming}
      />
    </>
  );
};

export default ChangeBowlerModal;
